"use client";

import * as TogglePrimitive from "@radix-ui/react-toggle";
import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";

import { cn } from "../lib/utils";
import { ICON_PRESET_CLASSES } from "./icon";

const toggleVariants = cva(
  "inline-flex items-center justify-center gap-2 rounded-md text-sm font-medium transition-colors hover:bg-ui-accent hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-ui-accent data-[state=on]:text-ui-accent-foreground [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        outline:
          "border border-input bg-transparent !text-foreground shadow-sm hover:bg-ui-accent hover:text-ui-accent-foreground",
      },
      size: {
        sm: `h-8 min-w-[4.625rem] px-3 text-xs ${ICON_PRESET_CLASSES.sizeForSVG.sm}`,
        default: `h-9 min-w-[5rem] px-4 py-2 ${ICON_PRESET_CLASSES.sizeForSVG.default}`,
        lg: `h-10 px-8 py-3 ${ICON_PRESET_CLASSES.sizeForSVG.lg}`,
        smIcon: `size-8 ${ICON_PRESET_CLASSES.sizeForSVG.sm}`,
        icon: `size-9 ${ICON_PRESET_CLASSES.sizeForSVG.default}`,
      },
      overrideClass: {
        true: "ui-toggle",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  },
);

/**
 * Radix UI (API reference): https://www.radix-ui.com/docs/primitives/components/toggle#api-reference
 *
 * shadcn/ui docs: https://ui.shadcn.com/docs/components/toggle
 */
const Toggle = React.forwardRef<
  React.ElementRef<typeof TogglePrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TogglePrimitive.Root> &
    VariantProps<typeof toggleVariants>
>(({ className, variant, size, overrideClass = true, ...props }, ref) => (
  <TogglePrimitive.Root
    className={cn(toggleVariants({ variant, size, overrideClass, className }))}
    ref={ref}
    {...props}
  />
));

Toggle.displayName = TogglePrimitive.Root.displayName;

type ToggleProps = React.ComponentProps<typeof TogglePrimitive.Root> &
  VariantProps<typeof toggleVariants>;

export { Toggle, toggleVariants, type ToggleProps };
